import debounce from 'lodash.debounce';
import BaseModule from '../../../javascripts/helpers/baseModule';
import scrollIntoViewport from '../../../javascripts/animation/scrollIntoViewport';

// Tabs module based on the wai-aria best practices https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html

const ACTIVE_CTA_CLAS = 'tabs__cta--active';
export default class Tabs extends BaseModule {
  constructor(element) {
    super(element, element.getAttribute('data-js-module'), [
      { id: 'ctaRight', selector: 'cta-right' },
      { id: 'ctaLeft', selector: 'cta-left' },
      { id: 'wrapper', selector: 'wrapper' },
      { id: 'tablist', selector: 'tablist' },
      { id: 'tabs', selector: 'tab' },
      { id: 'tabpanels', selector: 'tabpanel' }
    ]);
  }

  static activateTab(tab) {
    const tabPanelId = tab.getAttribute('aria-controls');
    document.getElementById(tabPanelId).removeAttribute('hidden');

    tab.removeAttribute('tabindex');
    tab.setAttribute('aria-selected', 'true');

    // if config triggerEvent provided dispatch custom event
    if (tab.dataset.triggerEvent) {
      const { id, event } = JSON.parse(tab.dataset.triggerEvent);
      const customEvent = new CustomEvent(event);
      const targetElement = document.querySelector(`#${id}`);
      targetElement.dispatchEvent(customEvent);
    }
  }

  enableRightCta(enable) {
    if (enable) {
      this.elements.ctaRight.classList.add(ACTIVE_CTA_CLAS);
    } else {
      this.elements.ctaRight.classList.remove(ACTIVE_CTA_CLAS);
    }
  }

  enableLeftCta(enable) {
    if (enable) {
      this.elements.ctaLeft.classList.add(ACTIVE_CTA_CLAS);
    } else {
      this.elements.ctaLeft.classList.remove(ACTIVE_CTA_CLAS);
    }
  }

  deactivateTabs() {
    this.elements.tabs.forEach((tab, tabIndex) => {
      this.elements.tabpanels[tabIndex].setAttribute('hidden', 'hidden');
      tab.setAttribute('tabindex', '-1');
      tab.setAttribute('aria-selected', 'false');
    });
  }

  // When a tablists aria-orientation is set to vertical,
  // only up and down arrow should function.
  // In all other cases only left and right arrow function.
  determineOrientation(event, tabIndex) {
    const { key } = event;
    const vertical = this.elements.tablist.getAttribute('aria-orientation') === 'vertical';

    if (vertical) {
      if (key === 'ArrowUp' || key === 'ArrowDown') {
        event.preventDefault();
        this.switchTabOnArrowPress(key, tabIndex);
      }
    } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
      event.preventDefault();
      this.switchTabOnArrowPress(key, tabIndex);
    }
  }

  // Either focus the next, previous, first, or last tab
  // depening on key pressed
  switchTabOnArrowPress(key, tabIndex) {
    const tabCount = this.elements.tabs.length;

    switch (key) {
      case 'ArrowUp':
      case 'ArrowRight': {
        const newIndex = (tabIndex + tabCount + 1) % tabCount;
        const nextTab = this.elements.tabs[newIndex];
        nextTab.focus();
        this.deactivateTabs();
        this.constructor.activateTab(nextTab);
        break;
      }
      case 'ArrowDown':
      case 'ArrowLeft': {
        const newIndex = (tabIndex + tabCount - 1) % tabCount;
        const previousTab = this.elements.tabs[newIndex];
        previousTab.focus();
        this.deactivateTabs();
        this.constructor.activateTab(previousTab);
        break;
      }
      default: {
        break;
      }
    }
  }

  /**
   * Handle scroll of fullscreen container
   * @returns {void}
   */
  handleScroll() {
    // TODO: Debounce or throttle updated state

    const { scrollWidth } = this.elements.tablist;
    const { width } = this.elements.wrapper.getBoundingClientRect();
    const { scrollLeft } = this.elements.wrapper;

    // Update controls
    if (scrollLeft <= 5) {
      this.enableLeftCta(false);
    } else if (scrollLeft + width >= scrollWidth - 5) {
      this.enableRightCta(false);
    } else {
      if (!this.elements.ctaLeft.classList.contains(ACTIVE_CTA_CLAS)) this.enableLeftCta(true);
      if (!this.elements.ctaRight.classList.contains(ACTIVE_CTA_CLAS)) this.enableRightCta(true);
    }
  }

  /**
   * Scroll a step to the left
   * @returns {void}
   */
  handlePrev() {
    const { scrollLeft } = this.elements.wrapper;
    const { width } = this.elements.wrapper.getBoundingClientRect();
    this.elements.wrapper.scrollTo({
      left: scrollLeft === 0 ? scrollLeft : scrollLeft - width / 2,
      top: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Scroll a step to the right
   * @returns {void}
   */
  handleNext() {
    const { scrollLeft } = this.elements.wrapper;
    const { width } = this.elements.wrapper.getBoundingClientRect();
    this.elements.wrapper.scrollTo({
      left: scrollLeft + width / 2,
      top: 0,
      behavior: 'smooth'
    });
  }

  /**
   * Check if tablist width is wider than wrapper width
   * and show Next Button if it is
   * @returns {void}
   */
  checkScreenWidth() {
    const { clientWidth: outerClientWidth } = this.elements.wrapper;
    const { scrollWidth: innerClientWidth } = this.elements.tablist;

    // Only show next button if container is scrollable
    if (innerClientWidth > outerClientWidth) {
      this.enableRightCta(true);

      const activeElement = this.elements.tabs.asList.find((element) => element.hasAttribute('aria-selected'));

      if (activeElement) {
        const { left } = activeElement.getBoundingClientRect();

        if (left >= outerClientWidth) {
          this.elements.wrapper.scrollTo({
            left: left - 50,
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    } else {
      this.enableRightCta(false);
    }
  }

  bindControlEvents() {
    // Resize Event
    window.addEventListener(
      'resize',
      debounce((e) => {
        this.checkScreenWidth();
      }, 100)
    );

    // Container Scroll Event
    this.elements.wrapper.addEventListener(
      'scroll',
      debounce((e) => {
        this.handleScroll();
      }, 100)
    );

    // CTA Click Events
    this.elements.ctaLeft.addEventListener('click', (event) => {
      event.preventDefault();
      this.handlePrev();
    });
    this.elements.ctaRight.addEventListener('click', (event) => {
      event.preventDefault();
      this.handleNext();
    });

    // Tab Button Click Events
    this.elements.tabs.forEach((tab, tabIndex) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();

        const clickTarget = event.currentTarget;

        this.deactivateTabs();
        this.constructor.activateTab(clickTarget);
      });

      // Handle keydown on tabs
      tab.addEventListener('keydown', (event) => {
        const firstTab = this.elements.tabs[0];
        const lastTab = this.elements.tabs[this.elements.tabs.length - 1];

        switch (event.key) {
          // When focus is on an tab, moves focus to the last tab.
          case 'End': {
            event.preventDefault();

            lastTab.focus();
            this.deactivateTabs();
            this.constructor.activateTab(lastTab);
            break;
          }
          // When focus is on an tab, moves focus to the first tab.
          case 'Home': {
            event.preventDefault();

            firstTab.focus();
            this.deactivateTabs();
            this.constructor.activateTab(firstTab);
            break;
          }
          // Up and down are in keydown
          // because we need to prevent page scroll >:)
          case 'ArrowUp':
          case 'ArrowDown': {
            this.determineOrientation(event, tabIndex);
            break;
          }
          default: {
            break;
          }
        }
      });

      // Handle keyup on tabs
      tab.addEventListener('keyup', (event) => {
        event.preventDefault();

        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowLeft': {
            this.determineOrientation(event, tabIndex);
            break;
          }
          default: {
            break;
          }
        }
      });
    });
  }

  checkURLParameter() {
    const urlParams = new URLSearchParams(window.location.search);

    // if url paramter for "tabid"
    if (urlParams.has('tabid')) {
      const tabId = urlParams.get('tabid');
      const tabButton = document.querySelector(`#tab${tabId}`);

      // if tab exists
      if (tabButton) {
        this.deactivateTabs();
        this.constructor.activateTab(tabButton);
        const tabPanelId = tabButton.getAttribute('aria-controls');
        const tabPanel = document.getElementById(tabPanelId);

        scrollIntoViewport(tabPanel, 500);
      }
    }
  }

  // triggered by BaseModule
  init() {
    this.bindControlEvents();
    this.checkScreenWidth();
    this.checkURLParameter();
  }
}
